import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronUp, Trophy } from 'lucide-react';
import { API_BASE_URL } from '../config';
import { useWebSocketContext } from '../contexts/WebSocketContext';

// Notification toast component that appears near the leaderboard button
const CompletionNotification = ({ notification, theme, isDarkMode, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Animate in
    requestAnimationFrame(() => setIsVisible(true));

    // Start exit animation after 4 seconds
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
    }, 4000);

    // Remove after exit animation completes
    const removeTimer = setTimeout(() => {
      onDismiss();
    }, 4500);

    return () => {
      clearTimeout(exitTimer);
      clearTimeout(removeTimer);
    };
  }, [onDismiss]);

  const modeLabel = notification.mode === 'hard' ? 'Challenge' : 
                    notification.mode === 'team' ? 'Team' : 'Normal';

  return (
    <div
      className={`
        absolute top-full mt-1 left-1/2 -translate-x-1/2 z-[60]
        pointer-events-none
        transition-all duration-500 ease-out
        ${isVisible && !isExiting ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}
      `}
    >
      <div className={`
        flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg border
        whitespace-nowrap text-xs font-medium
        ${isDarkMode 
          ? 'bg-emerald-900/90 border-emerald-600/50 text-emerald-200' 
          : 'bg-emerald-50/95 border-emerald-300 text-emerald-800'
        }
      `}>
        <Trophy className={`w-3.5 h-3.5 flex-shrink-0 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-500'}`} />
        <span>
          <strong>{notification.userId}</strong> completed Level {notification.level} ({modeLabel})
        </span>
        <span className={`
          inline-block w-2 h-2 rounded-full animate-ping
          ${isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'}
        `} />
      </div>
    </div>
  );
};

// Pulsing indicator dot on the leaderboard button
const LiveIndicator = ({ isActive, isDarkMode }) => {
  if (!isActive) return null;
  
  return (
    <span className="relative flex h-2 w-2 ml-1">
      <span className={`
        animate-ping absolute inline-flex h-full w-full rounded-full opacity-75
        ${isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'}
      `} />
      <span className={`
        relative inline-flex rounded-full h-2 w-2
        ${isDarkMode ? 'bg-emerald-400' : 'bg-emerald-500'}
      `} />
    </span>
  );
};

const LeaderboardTable = ({ data, theme, mode }) => (
  <div className="overflow-x-auto">
    <table className={`w-full text-sm ${theme.container}`}>
      <thead>
        <tr className={`${theme.containerBg} border-b ${theme.border}`}>
          <th className={`px-4 py-2 text-left w-16 ${theme.text}`}>
            Rank
          </th>
          <th className={`px-4 py-2 text-left ${theme.text}`}>
            Player ID
          </th>
          <th className={`px-4 py-2 text-right w-24 ${theme.text}`}>
            Level
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map((player, index) => (
          <tr 
            key={`${player.user_id}-${mode}`}
            className={`border-b ${theme.border} ${theme.containerHover}`}
          >
            <td className={`px-4 py-2 w-16 ${theme.text}`}>
              #{index + 1}
            </td>
            <td className={`px-4 py-2 font-medium overflow-hidden text-ellipsis ${theme.text}`}>
              {player.user_id}
            </td>
            <td className={`px-4 py-2 text-right w-24 ${theme.text}`}>
              {player.current_level}
            </td>
          </tr>
        ))}
        {data.length === 0 && (
          <tr>
            <td 
              colSpan="3" 
              className={`px-4 py-4 text-center ${theme.text}`}
            >
              No data available for {mode} mode
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const TeamLeaderboardTable = ({ data, theme, hoveredTeam, setHoveredTeam }) => (
  <div className="overflow-x-auto">
    <table className={`w-full text-sm ${theme.container}`}>
      <thead>
        <tr className={`${theme.containerBg} border-b ${theme.border}`}>
          <th className={`px-4 py-2 text-left w-16 ${theme.text}`}>
            Rank
          </th>
          <th className={`px-4 py-2 text-left ${theme.text}`}>
            Team Name
          </th>
          <th className={`px-4 py-2 text-right w-24 ${theme.text}`}>
            Level
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map((team, index) => (
          <tr 
            key={`${team.team_name}-${index}`}
            className={`border-b ${theme.border} ${theme.containerHover} relative`}
            onMouseEnter={() => setHoveredTeam(index)}
            onMouseLeave={() => setHoveredTeam(null)}
          >
            <td className={`px-4 py-2 w-16 ${theme.text}`}>
              #{index + 1}
            </td>
            <td className={`px-4 py-2 font-medium overflow-hidden text-ellipsis ${theme.text} relative`}>
              {team.team_name}
              {hoveredTeam === index && team.members && team.members.length > 0 && (
                <div className={`fixed ${theme.container} border ${theme.border} rounded-md shadow-xl p-3 min-w-48 max-w-64`} 
                     style={{ 
                       zIndex: 9999,
                       left: '50%',
                       top: '50%',
                       transform: 'translate(-50%, -50%)'
                     }}>
                  <div className={`text-xs font-semibold mb-2 ${theme.text}`}>Team Members:</div>
                  {team.members.map((member, memberIndex) => (
                    <div key={memberIndex} className={`text-xs ${theme.subtext} py-0.5`}>
                      {member}
                    </div>
                  ))}
                </div>
              )}
            </td>
            <td className={`px-4 py-2 text-right w-24 ${theme.text}`}>
              {team.highest_level}
            </td>
          </tr>
        ))}
        {data.length === 0 && (
          <tr>
            <td 
              colSpan="3" 
              className={`px-4 py-4 text-center ${theme.text}`}
            >
              No team data available
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const StatsLeaderboardTable = ({ data, theme, hoveredEntry, setHoveredEntry }) => (
  <div className="overflow-x-auto">
    <table className={`w-full text-sm ${theme.container}`}>
      <thead>
        <tr className={`${theme.containerBg} border-b ${theme.border}`}>
          <th className={`px-2 py-2 text-left w-12 ${theme.text}`}>
            Rank
          </th>
          <th className={`px-2 py-2 text-left ${theme.text}`}>
            Player ID
          </th>
          <th className={`px-2 py-2 text-right w-16 ${theme.text}`}>
            Moves
          </th>
          <th className={`px-2 py-2 text-right w-16 ${theme.text}`}>
            Undos
          </th>
          <th className={`px-2 py-2 text-right w-16 ${theme.text}`}>
            Restarts
          </th>
          <th className={`px-2 py-2 text-right w-16 ${theme.text}`}>
            Tubes
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map((player, index) => (
          <tr 
            key={`${player.user_id}-${player.entry_type}-stats`}
            className={`border-b ${theme.border} ${theme.containerHover} relative`}
            onMouseEnter={() => player.entry_type === 'team' ? setHoveredEntry(index) : null}
            onMouseLeave={() => player.entry_type === 'team' ? setHoveredEntry(null) : null}
          >
            <td className={`px-2 py-2 w-12 ${theme.text}`}>
              #{index + 1}
            </td>
            <td className={`px-2 py-2 font-medium overflow-hidden text-ellipsis ${theme.text} relative`}>
              {player.entry_type === 'team' ? (
                <>
                  Team: {player.team_name || player.user_id}
                  {hoveredEntry === index && player.team_members && player.team_members.length > 0 && (
                    <div className={`fixed ${theme.container} border ${theme.border} rounded-md shadow-xl p-3 min-w-48 max-w-64`} 
                         style={{ 
                           zIndex: 9999,
                           left: '50%',
                           top: '50%',
                           transform: 'translate(-50%, -50%)'
                         }}>
                      <div className={`text-xs font-semibold mb-2 ${theme.text}`}>Team Members:</div>
                      {player.team_members.map((member, memberIndex) => (
                        <div key={memberIndex} className={`text-xs ${theme.subtext} py-0.5`}>
                          {member}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                player.user_id
              )}
            </td>
            <td className={`px-2 py-2 text-right w-16 ${theme.text}`}>
              {player.total_moves || 0}
            </td>
            <td className={`px-2 py-2 text-right w-16 ${theme.text}`}>
              {player.total_undos || 0}
            </td>
            <td className={`px-2 py-2 text-right w-16 ${theme.text}`}>
              {player.total_restarts || 0}
            </td>
            <td className={`px-2 py-2 text-right w-16 ${theme.text}`}>
              {player.total_tubes_added || 0}
            </td>
          </tr>
        ))}
        {data.length === 0 && (
          <tr>
            <td 
              colSpan="6" 
              className={`px-4 py-4 text-center ${theme.text}`}
            >
              No stats data available
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const Leaderboard = ({ theme, isDarkMode, userId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('normal');
  const [normalData, setNormalData] = useState([]);
  const [hardData, setHardData] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredTeam, setHoveredTeam] = useState(null);
  const [hoveredEntry, setHoveredEntry] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showLiveIndicator, setShowLiveIndicator] = useState(false);
  const liveIndicatorTimerRef = useRef(null);
  const notificationIdRef = useRef(0);

  // WebSocket connection for live updates via context
  const { sendMessage, isConnected } = useWebSocketContext();

  const dismissNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Handle WebSocket messages
  useEffect(() => {
    const handleWebSocketMessage = (event) => {
      const data = event.detail;
      
      if (data.type === 'LEADERBOARD_UPDATE' || data.type === 'LEADERBOARD_DATA') {
        if (data.data) {
          setNormalData(data.data.normal || []);
          setHardData(data.data.hard || []);
          setTeamData(data.data.team || []);
          setStatsData(data.data.stats || []);
          setIsLoading(false);
          setError(null);
        }

        // Show completion notification if another user finished a level
        if (data.type === 'LEADERBOARD_UPDATE' && data.completionInfo) {
          const info = data.completionInfo;
          
          // Don't show notification for the current user's own completions
          if (info.userId !== userId) {
            const notificationId = ++notificationIdRef.current;
            setNotifications(prev => {
              // Keep only the most recent 3 notifications
              const updated = [...prev, {
                id: notificationId,
                userId: info.userId,
                level: info.level,
                mode: info.mode,
                timestamp: info.timestamp
              }];
              return updated.slice(-3);
            });

            // Show the live indicator pulse on the button
            setShowLiveIndicator(true);
            if (liveIndicatorTimerRef.current) {
              clearTimeout(liveIndicatorTimerRef.current);
            }
            liveIndicatorTimerRef.current = setTimeout(() => {
              setShowLiveIndicator(false);
            }, 5000);
          }
        }
      }
    };

    window.addEventListener('websocket-message', handleWebSocketMessage);
    
    return () => {
      window.removeEventListener('websocket-message', handleWebSocketMessage);
      if (liveIndicatorTimerRef.current) {
        clearTimeout(liveIndicatorTimerRef.current);
      }
    };
  }, [userId]);

  // Fetch leaderboards via WebSocket or fallback to API
  const fetchLeaderboards = async () => {
    if (!isOpen) return;
    
    setIsLoading(true);
    setError(null);
    
    // Try WebSocket first if connected
    if (isConnected && sendMessage) {
      const success = sendMessage({
        type: 'REQUEST_LEADERBOARD'
      });
      
      if (success) {
        // WebSocket request sent, wait for response
        return;
      }
    }
    
    // Fallback to API if WebSocket is not available
    try {
      const [normalResponse, hardResponse, teamResponse, statsResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/leaderboard`),
        fetch(`${API_BASE_URL}/leaderboard/hard`),
        fetch(`${API_BASE_URL}/leaderboard/team`),
        fetch(`${API_BASE_URL}/leaderboard/stats`)
      ]);

      if (!normalResponse.ok || !hardResponse.ok || !teamResponse.ok || !statsResponse.ok) {
        throw new Error('Failed to fetch leaderboard data');
      }

      const [normalData, hardData, teamData, statsData] = await Promise.all([
        normalResponse.json(),
        hardResponse.json(),
        teamResponse.json(),
        statsResponse.json()
      ]);

      setNormalData(normalData || []);
      setHardData(hardData || []);
      setTeamData(teamData || []);
      setStatsData(statsData || []);
    } catch (err) {
      console.error('Error loading leaderboards:', err);
      setError('Failed to load leaderboard data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboards();
  }, [isOpen, isConnected]);

  // Handle the case where theme or isDarkMode props are missing
  const getContainerClass = () => {
    const baseClasses = 'absolute top-full mt-1 w-[400px] rounded-lg shadow-lg z-50 left-1/2 -translate-x-1/2 border';
    return `${baseClasses} ${
      isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`;
  };

  // Handle the case where theme object properties are missing
  const getThemeClass = (type) => {
    if (!theme) return '';
    return theme[type] || '';
  };

  return (
    <div className="relative w-full flex flex-col items-center">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          group flex items-center gap-1 px-2 py-1 
          ${theme.text} hover:opacity-80 transition-colors
        `}
      >
        <span className="text-sm font-medium">Leaderboard</span>
        <LiveIndicator isActive={showLiveIndicator} isDarkMode={isDarkMode} />
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>

      {/* Completion notifications - shown near the leaderboard button */}
      {notifications.map((notification) => (
        <CompletionNotification
          key={notification.id}
          notification={notification}
          theme={theme}
          isDarkMode={isDarkMode}
          onDismiss={() => dismissNotification(notification.id)}
        />
      ))}

      {isOpen && (
        <div className={`
          absolute top-full mt-1 w-[600px] ${theme.container}
          rounded-lg shadow-lg z-50 left-1/2 -translate-x-1/2 border ${theme.border}
        `}>
          <div className="p-4">
            <div className="flex mb-4 border-b border-gray-200">
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'normal'
                    ? `${theme.text} border-b-2 border-blue-500`
                    : `${theme.subtext} hover:${theme.text} hover:opacity-80`
                }`}
                onClick={() => setActiveTab('normal')}
              >
                Normal Mode
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'hard'
                    ? `${theme.text} border-b-2 border-blue-500`
                    : `${theme.subtext} hover:${theme.text} hover:opacity-80`
                }`}
                onClick={() => setActiveTab('hard')}
              >
                Challenge Mode
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'team'
                    ? `${theme.text} border-b-2 border-blue-500`
                    : `${theme.subtext} hover:${theme.text} hover:opacity-80`
                }`}
                onClick={() => setActiveTab('team')}
              >
                Team Mode
              </button>
              <button
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === 'stats'
                    ? `${theme.text} border-b-2 border-blue-500`
                    : `${theme.subtext} hover:${theme.text} hover:opacity-80`
                }`}
                onClick={() => setActiveTab('stats')}
              >
                Move Stats
              </button>
            </div>

            {isLoading ? (
              <div className={`text-center py-4 ${theme.text}`}>
                Loading...
              </div>
            ) : error ? (
              <div className="text-red-500 text-center py-4">
                {error}
              </div>
            ) : (
              <>
                {activeTab === 'normal' && (
                  <LeaderboardTable 
                    data={normalData}
                    theme={theme}
                    mode="normal"
                  />
                )}
                {activeTab === 'hard' && (
                  <LeaderboardTable 
                    data={hardData}
                    theme={theme}
                    mode="hard"
                  />
                )}
                {activeTab === 'team' && (
                  <TeamLeaderboardTable 
                    data={teamData}
                    theme={theme}
                    hoveredTeam={hoveredTeam}
                    setHoveredTeam={setHoveredTeam}
                  />
                )}
                {activeTab === 'stats' && (
                  <StatsLeaderboardTable 
                    data={statsData}
                    theme={theme}
                    hoveredEntry={hoveredEntry}
                    setHoveredEntry={setHoveredEntry}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
